/**
 * ColorPickerInput  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { FieldNumber, FieldText, IFieldTextProps } from "../../fields";
import { BEM } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";

export interface IColorPickerInputProps {
	className?: string;
	type?: "text" | "number";
	value?: string;
	onChange?: (value: string) => void;
	minLength?: number;
	maxLength?: number;
	min?: number;
	max?: number;
	step?: string;
	suffix?: string;
	autoFocus?: boolean;
	autoFocusSelect?: boolean;
}

export const ColorPickerInput: React.FC<IColorPickerInputProps> = ({
	className,
	value,
	onChange,
	minLength,
	maxLength,
	type,
	min,
	max,
	step,
	suffix,
	autoFocus,
	autoFocusSelect
}) => {
	const [ currentValue, setCurrentValue ] = useState<string>(value);
	const [ queuedValue, setQueuedValue ] = useState<string>(value);
	const inputRef = useRef<HTMLInputElement>();

	const baseClass = BEM("edt-color-picker-input");

	const handleChange = useCallback<(value: string | number) => void>(
		(value) => {
			setCurrentValue(`${value}`);

			if (isFunction(onChange)) {
				onChange(String(value));
			}
		},
		[ onChange ]
	);

	const handleBlur = useCallback<IFieldTextProps["onBlur"]>(() => {
		if (queuedValue) {
			setCurrentValue(queuedValue);
			setQueuedValue(null);
		}
	}, [ queuedValue ]);

	useEffect(() => {
		if (document?.activeElement === inputRef.current) {
			setQueuedValue(value);
			return;
		}

		setCurrentValue(value);
		setQueuedValue(value);
	}, [ value ]);

	if (className) {
		baseClass.add(className);
	}

	if (type === "number") {
		return (
			<FieldNumber
				value={parseFloat(currentValue)}
				className={baseClass.toClassName()}
				onChange={handleChange}
				onBlur={handleBlur}
				inputRef={inputRef}
				min={min}
				max={max}
				step={step}
				hideButtons
				suffix={suffix}
			/>
		);
	}

	return (
		<FieldText
			value={currentValue}
			className={baseClass.toClassName()}
			onChange={handleChange}
			onBlur={handleBlur}
			inputRef={inputRef}
			minLength={minLength}
			maxLength={maxLength}
			autoFocus={autoFocus}
			autoFocusSelect={autoFocusSelect}
		/>
	);
};

ColorPickerInput.defaultProps = {
	type: "text"
};
